<template>
  <div
    v-test-id="'error-state'"
    :class="[
      'tm-error-state',
      {
        [`tm-error-state--${kebabSize}`]: size,
        'tm-error-state--full-height': fullHeight,
        'tm-error-state--full-page-height': fullPageHeight,
      },
    ]"
  >
    <div class="text-center">
      <div class="d-flex justify-center">
        <tm-icon
          class="mb-4 gray4--text"
          :name="icon"
          custom-size="80px"
        />
      </div>

      <h2 class="page-title-20 lh-24">
        {{ title || $t('errors.itLooksLikeSomethingWentWrong') }}
      </h2>

      <div class="mb-6 lh-18 distinct--text">
        {{ description || $t('errors.pleaseTryAgainInAFewMinutesOrRefreshThePageNow') }}
      </div>

      <div class="d-flex justify-center">
        <tm-button
          v-test-id="'error-state-retry'"
          color="primary"
          class="min-w150 min-h40"
          @click="$emit('on-retry')"
        >
          <tm-icon
            v-if="retryIcon"
            :name="retryIcon"
            left
            size="small"
          />
          {{ retryText || $t('common.tryAgain') }}
        </tm-button>

        <slot name="after-button" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { kebabCase } from 'lodash-es'
import { computed } from '@/composition/vue/compositionApi'
import type { Size } from '@/components/shared/types'
import TmButton from '@/components/shared/TmButton.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import type { IconName } from '@/assets/icons/icons'

const {
  size = '',
  icon = 'error_outline',
  retryIcon = 'refresh',
} = defineProps<{
  fullHeight?: boolean
  fullPageHeight?: boolean
  size?: Extract<Size, '' | 'small' | 'medium'>
  icon?: IconName
  title?: string
  description?: string
  retryIcon?: IconName
  retryText?: string
}>()

defineEmits<{
  'on-retry': []
}>()

const kebabSize = computed(() => kebabCase(size))
</script>

<style lang="scss" scoped>
.min-w150 {
  min-width: 150px;
}
.min-h40 {
  min-height: 40px;
}
.gray4--text {
  color: $gray4;
}

.tm-error-state {
  display: flex;
  justify-content: center;
  align-items: center;

  &--small {
    min-height: 230px;
  }

  &--medium {
    min-height: 480px;
  }

  &--full-height {
    min-height: 100%;
  }

  &--full-page-height {
    min-height: 100vh;
  }
}
</style>
